import { useState, useEffect, useRef } from 'react';
import { getUserProfile } from '../services/userService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

// Simple cache to avoid redundant fetches
const profileCache = new Map();
const CACHE_DURATION = 300000; // 5 minutes (increased for better performance)

export const useUserProfile = (userId) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    
    if (!userId) {
      setLoading(false);
      return;
    }

    // Set up real-time listener FIRST for immediate updates when admin makes changes
    // This ensures real-time updates take priority over cache
    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(
      userRef,
      (docSnapshot) => {
        if (docSnapshot.exists() && mountedRef.current) {
          const profile = { id: docSnapshot.id, ...docSnapshot.data() };
          
          // Check if account is deleted (legacy check for marked-as-deleted accounts)
          if (profile.isDeleted) {
            setUserProfile(null);
            setError('Your account has been deleted. Please contact support.');
            // Clear cache
            profileCache.delete(userId);
            return;
          }
          
          // Always update profile from real-time listener (bypasses cache)
          setUserProfile(profile);
          // Update cache with latest data
          profileCache.set(userId, {
            data: profile,
            timestamp: Date.now()
          });
          setLoading(false);
        } else if (mountedRef.current) {
          // Document doesn't exist - account has been completely deleted
          setUserProfile(null);
          setError('Your account has been deleted. Please contact support.');
          // Clear cache
          profileCache.delete(userId);
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error in real-time listener:', error);
        // Fallback to regular fetch if listener fails
        if (mountedRef.current) {
          getUserProfile(userId).then(profile => {
            if (profile && mountedRef.current) {
              setUserProfile(profile);
              profileCache.set(userId, {
                data: profile,
                timestamp: Date.now()
              });
            }
          }).catch(err => {
            console.error('Fallback profile fetch error:', err);
            if (mountedRef.current) {
              setError(err.message);
            }
          });
        }
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    );

    // Check cache for initial display while real-time listener loads
    const cached = profileCache.get(userId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      // Use cached data for initial render, but real-time listener will update it
      if (mountedRef.current) {
        setUserProfile(cached.data);
        setLoading(false);
      }
    }

    // Initial load if not cached (fallback if real-time listener hasn't fired yet)
    if (!cached) {
      const loadProfile = async () => {
        try {
          setLoading(true);
          setError(null);
          const profile = await getUserProfile(userId);
          
          if (mountedRef.current) {
            if (profile) {
              // Check if account is deleted
              if (profile.isDeleted) {
                setUserProfile(null);
                setError('Your account has been deleted. Please contact support.');
                profileCache.delete(userId);
                return;
              }
              
              setUserProfile(profile);
              // Cache the result
              profileCache.set(userId, {
                data: profile,
                timestamp: Date.now()
              });
            } else {
              console.warn(`User profile not found for ${userId}. Creating default profile...`);
              // Try to create a default profile if it doesn't exist
              try {
                const { createUserProfile } = await import('../services/userService');
                const { auth } = await import('../firebase/config');
                const currentUser = auth.currentUser;
                
                if (currentUser && currentUser.uid === userId) {
                  const defaultProfile = await createUserProfile(userId, {
                    email: currentUser.email || '',
                    username: currentUser.email?.split('@')[0] || 'User',
                    vipLevel: 'VIP1',
                    creditScore: 100,
                    balances: {
                      wallet: 0,
                      frozen: 0,
                      commission: 0,
                      total: 0,
                      todaysProfit: 0,
                      salary: 0
                    }
                  });
                  setUserProfile(defaultProfile);
                  profileCache.set(userId, {
                    data: defaultProfile,
                    timestamp: Date.now()
                  });
                } else {
                  setError('User profile not found. Please contact support.');
                }
              } catch (createErr) {
                console.error('Error creating default profile:', createErr);
                setError('User profile not found. Please contact support.');
              }
            }
          }
        } catch (err) {
          console.error('Error loading user profile:', err);
          if (mountedRef.current) {
            setError(err.message);
          }
        } finally {
          if (mountedRef.current) {
            setLoading(false);
          }
        }
      };

      loadProfile();
    }

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [userId]);

  const refreshProfile = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      const profile = await getUserProfile(userId);
      
      setUserProfile(profile);
      // Update cache
      profileCache.set(userId, {
        data: profile,
        timestamp: Date.now()
      });
    } catch (err) {
      console.error('Error refreshing user profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Clear cache for a specific user (useful after updates)
  const clearCache = () => {
    if (userId) {
      profileCache.delete(userId);
    }
  };

  return { userProfile, loading, error, refreshProfile, clearCache };
};

// Export function to clear cache globally
export const clearProfileCache = (userId) => {
  if (userId) {
    profileCache.delete(userId);
  } else {
    profileCache.clear();
  }
};
